import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";

export const useDefaultDraft = <Source, Draft>({
  defaultSource,
  createDraft,
  normaliseDraft,
  sourcesMatch,
}: {
  defaultSource: Source;
  createDraft: (source: Source) => Draft;
  normaliseDraft: (source: Source, proposed: Draft) => Draft | null;
  sourcesMatch: (first: Source, second: Source) => boolean;
}) => {
  const [snapshot, setSnapshot] = useState<{
    source: Source;
    draft: Draft;
  } | null>(null);
  const source = snapshot?.source ?? defaultSource;
  const initialDraft = useMemo(
    () => createDraft(source),
    [createDraft, source]
  );
  const draft = snapshot?.draft ?? initialDraft;
  const setDraft: Dispatch<SetStateAction<Draft>> = useCallback(
    (update) => {
      setSnapshot((current) => {
        const source = current?.source ?? defaultSource;
        const proposed =
          typeof update === "function"
            ? (update as (previous: Draft) => Draft)(
                current?.draft ?? createDraft(source)
              )
            : update;
        const draft = normaliseDraft(source, proposed);
        return draft === null ? null : { source, draft };
      });
    },
    [defaultSource, createDraft, normaliseDraft]
  );
  const discard = useCallback(() => {
    setSnapshot(null);
  }, []);
  const save = useCallback(
    async (commit: (source: Source, draft: Draft) => Promise<unknown>) => {
      await commit(source, draft);
      setSnapshot((current) => (current === snapshot ? null : current));
    },
    [source, draft, snapshot]
  );
  return {
    source,
    draft,
    setDraft,
    discard,
    save,
    isStale: !sourcesMatch(source, defaultSource),
  };
};
